import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { BookingCaseContextProvider } from '@frontend/features/travel-services/components/BookingCaseContext';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Alert, Box, Button, ListSubheader, MenuItem, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONFIG } from '@frontend/config';
import { client } from '@frontend/features/common/services';
import { HotelAgencyContext } from './HotelAgencyContext';
import { createHotelSearchTransport } from './hotelSearchTransport';
import { HotelSearchPage } from './HotelSearchPage';

type Context = { id: number; name: string };
export function HotelSearchContextSelector() {
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const preferenceKey = `hotel-search-agency:${myProfile?.id ?? 'anonymous'}`;
  const [locked, setLocked] = useState(false);
  const contexts = useQuery(
    ['hotel-search-contexts', myProfile?.id],
    async () => (await client.get<{ data: Context[]; sales_agencies: number[] }>('hotels/search-contexts')).data,
    { enabled: !!myProfile?.id, staleTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false },
  );
  const [selected, setSelected] = useState<number>(() => {
    try {
      return Number(sessionStorage.getItem(preferenceKey)) || Number(CONFIG.AGENCY_ID);
    } catch {
      return Number(CONFIG.AGENCY_ID);
    }
  });
  const agency =
    contexts.data?.data.find((item) => item.id === selected)?.id ??
    contexts.data?.data.find((item) => item.id === Number(CONFIG.AGENCY_ID))?.id ??
    contexts.data?.data[0]?.id;
  const transport = useMemo(() => createHotelSearchTransport(agency), [agency]);
  if (!myProfile?.id || contexts.isLoading) return <Alert severity="info">{t('hotelConnections.loading')}</Alert>;
  if (contexts.isError) return (
    <Alert severity="error">
      {t('hotelConnections.error')}
      <Button onClick={() => contexts.refetch()}>{t('hotelConnections.retry')}</Button>
    </Alert>
  );
  if (!agency) return <Alert severity="info">{t('hotelSharing.noContexts')}</Alert>;
  const connectionSelector = (
    <Tooltip title={t('hotelSharing.contextHint')} arrow>
      <Box sx={{ width: { xs: '100%', md: 240 }, maxWidth: '100%', minWidth: 0 }}>
        <TextField
          select
          size="small"
          sx={{
            '& .MuiInputBase-root': { bgcolor: 'background.paper' },
            '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
          fullWidth
          label={t('hotelSharing.searchWith')}
          value={agency}
          disabled={locked}
          onChange={(event) => {
            const next = Number(event.target.value);
            setSelected(next);
            try {
              sessionStorage.setItem(preferenceKey, String(next));
            } catch {
              /* optional preference */
            }
          }}
        >
          <ListSubheader sx={{ whiteSpace: 'normal', lineHeight: 1.5, py: 1, maxWidth: 320 }}>
            {t('hotelSharing.contextHint')}
          </ListSubheader>
          {contexts.data?.data.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Tooltip>
  );
  return (
    <HotelAgencyContext.Provider value={agency}>
      <BookingCaseContextProvider allowedAgencyIds={contexts.data?.sales_agencies}>
        <HotelSearchPage
          key={`${myProfile?.id}:${agency}`}
          transport={transport}
          onContextLockedChange={setLocked}
          headerActions={connectionSelector}
        />
      </BookingCaseContextProvider>
    </HotelAgencyContext.Provider>
  );
}
